/**
 * Package Catalog (V3 screen S13) — a READ projection of the unified package
 * store (skill / contour / bet / frame / principle packages) with lifecycle and
 * deployment state. Mirrors the DecisionRoom surface: a single `api.get` packet
 * fetch, a `PageHeader`, a lens/type switcher, and the shared eight-state render
 * contract (`RenderState`) for loading / empty / normal / stale / overflow /
 * redacted / forbidden / error.
 *
 * Lens/projection doctrine (CLAUDE.md): the catalog never mutates business
 * truth. Row actions deep-link to the canonical package detail (where the
 * server enforces install/approval policy); they do not POST from here. The
 * compatibility/install wizard is a PROPOSED module — surfaced honestly, not
 * faked — and degrades to a truthful empty state when its read endpoint is
 * absent.
 *
 * Backend consumed (read only): `GET /api/packages` (the §5 list endpoint,
 * `{ items, total }`, filterable by `package_type`). The per-context deployment
 * detail (`/api/package-deployments/context/:contextId`) needs a context id, so
 * it is NOT loaded by the catalog; deployment counts shown here come from the
 * package projection and the inspector marks deployment fields as proposed.
 */
import * as React from "react";
import { useGo } from "@refinedev/core";

import {
  PACKAGE_TYPES,
  type Package,
  type PackageType,
} from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/app/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Field, FieldGrid, InspectorSection, RefLink } from "@/components/inspector";
import { RenderState, type RenderStateKind } from "@/components/app/RenderState";
import { api } from "@/lib/api";
import { formatDateTime, formatNumber, humanize } from "@/lib/format";

/** The catalog lens: "all" plus each of the five package types. */
type CatalogLens = "all" | PackageType;

/** Lens order shown in the type switcher (matches the S13 mockup tab strip). */
const LENSES: readonly CatalogLens[] = ["all", ...PACKAGE_TYPES];

/** Page size requested from the list endpoint (overflow is disclosed above it). */
const PAGE_SIZE = 50;

/** Per-package-type icon, all routed through the shared icon registry. */
const TYPE_ICON: Record<PackageType, IconName> = {
  skill: "skills",
  contour: "processes",
  bet: "bets",
  frame: "frames",
  principle: "principles",
};

/** Map a package's maturity stage to a coarse compatibility read for display. */
function compatibilityOf(pkg: Package): string {
  switch (pkg.maturity_stage) {
    case "executable":
    case "infrastructure":
    case "operational":
      return "compatible";
    case "deprecated":
      return "deprecated — review required";
    default:
      return "requires review";
  }
}

/* ------------------------------------------------------------------ data fetch */

interface CatalogData {
  readonly items: Package[];
  readonly total: number;
}

function usePackageCatalog(lens: CatalogLens) {
  const [data, setData] = React.useState<CatalogData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const reload = React.useCallback(() => setReloadKey((value) => value + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const query: Record<string, string | number> = { pageSize: PAGE_SIZE };
    if (lens !== "all") query.package_type = lens;
    api
      .get<CatalogData>("/packages", { query })
      .then((packet) => {
        if (!cancelled) setData(packet);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load Package Catalog.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [lens, reloadKey]);

  return { data, loading, error, reload };
}

/* ------------------------------------------------------------------ subviews */

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="text-xs font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <p className="font-mono text-sm tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

function PackageRecord({
  pkg,
  selected,
  onSelect,
  onOpen,
}: {
  pkg: Package;
  selected: boolean;
  onSelect: () => void;
  onOpen: () => void;
}) {
  return (
    <Card
      data-testid="catalog-record"
      className={selected ? "border-foreground" : undefined}
      onClick={onSelect}
    >
      <CardHeader className="p-4 pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-base [overflow-wrap:anywhere]">{pkg.name}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {humanize(pkg.package_type)} package · {compatibilityOf(pkg)}
            </p>
          </div>
          <Badge variant="outline">
            <Icon name={TYPE_ICON[pkg.package_type]} aria-hidden />
            {humanize(pkg.package_type)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={pkg.status} />
          <Badge variant="secondary">{humanize(pkg.maturity_stage)}</Badge>
          <Badge variant="outline">v{pkg.version}</Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              onOpen();
            }}
          >
            <Icon name="deepLink" data-icon="inline-start" aria-hidden />
            Open package
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function CatalogTable({ items }: { items: Package[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Package</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>State</TableHead>
          <TableHead>Maturity</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((pkg) => (
          <TableRow key={pkg.id} data-testid="catalog-row">
            <TableCell className="font-medium [overflow-wrap:anywhere]">{pkg.name}</TableCell>
            <TableCell>
              <Badge variant="outline">{humanize(pkg.package_type)}</Badge>
            </TableCell>
            <TableCell>
              <StatusBadge status={pkg.status} />
            </TableCell>
            <TableCell className="text-muted-foreground">
              {humanize(pkg.maturity_stage)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CatalogInspector({
  selected,
  total,
  publishedCount,
  deprecatedCount,
}: {
  selected: Package | null;
  total: number;
  publishedCount: number;
  deprecatedCount: number;
}) {
  return (
    <div className="flex flex-col gap-6">
      <InspectorSection title="Selected package">
        {selected ? (
          <FieldGrid>
            <Field label="Name">
              <RefLink resource="packages" id={selected.id} label={selected.name} />
            </Field>
            <Field label="Type">{humanize(selected.package_type)}</Field>
            <Field label="State">
              <StatusBadge status={selected.status} />
            </Field>
            <Field label="Maturity">{humanize(selected.maturity_stage)}</Field>
            <Field label="Version">v{selected.version}</Field>
            <Field label="Compatibility">{compatibilityOf(selected)}</Field>
            <Field label="Domain">{selected.domain ?? "—"}</Field>
            <Field label="Updated">{formatDateTime(selected.updated_at)}</Field>
          </FieldGrid>
        ) : (
          <p className="text-sm text-muted-foreground">
            Select a package from the catalog to inspect its fields, lifecycle, and
            compatibility.
          </p>
        )}
      </InspectorSection>

      <InspectorSection title="Operating metrics">
        <div className="grid grid-cols-2 gap-3">
          <Metric label="In view" value={formatNumber(total)} />
          <Metric label="Published" value={formatNumber(publishedCount)} />
          <Metric label="Deprecated" value={formatNumber(deprecatedCount)} />
          <Metric label="Types" value={PACKAGE_TYPES.length} />
        </div>
      </InspectorSection>

      <InspectorSection title="Proposed module">
        <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          The install wizard and compatibility checker are not built yet. Deployment
          counts and install paths are enforced server-side via the package-deployments
          runtime; the catalog is a read-only projection.
        </div>
      </InspectorSection>
    </div>
  );
}

/* ------------------------------------------------------------------ workspace */

function CatalogWorkspace({
  data,
  selected,
  onSelect,
  onOpen,
}: {
  data: CatalogData;
  selected: Package | null;
  onSelect: (pkg: Package) => void;
  onOpen: (pkg: Package) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(280px,0.9fr)]">
      <div className="flex flex-col gap-6">
        <InspectorSection title="Catalog">
          <div className="flex flex-col gap-3">
            {data.items.map((pkg) => (
              <PackageRecord
                key={pkg.id}
                pkg={pkg}
                selected={selected?.id === pkg.id}
                onSelect={() => onSelect(pkg)}
                onOpen={() => onOpen(pkg)}
              />
            ))}
          </div>
        </InspectorSection>

        <InspectorSection title="Packages">
          <CatalogTable items={data.items} />
        </InspectorSection>
      </div>

      <CatalogInspector
        selected={selected}
        total={data.total}
        publishedCount={data.items.filter((p) => p.status === "published").length}
        deprecatedCount={data.items.filter((p) => p.maturity_stage === "deprecated").length}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ page */

export function PackageCatalogPage() {
  const go = useGo();
  const [lens, setLens] = React.useState<CatalogLens>("all");
  const [query, setQuery] = React.useState("");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const { data, loading, error, reload } = usePackageCatalog(lens);

  // Client-side name filter over the authorized projection (the server already
  // scoped by type; this is a view-only refinement, never a truth mutation).
  const filtered = React.useMemo<Package[]>(() => {
    if (!data) return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return data.items;
    return data.items.filter((pkg) => pkg.name.toLowerCase().includes(needle));
  }, [data, query]);

  const selected = React.useMemo<Package | null>(
    () => filtered.find((pkg) => pkg.id === selectedId) ?? null,
    [filtered, selectedId],
  );

  const state: RenderStateKind = loading
    ? "loading"
    : error
      ? "error"
      : filtered.length === 0
        ? "empty"
        : "normal";

  // Disclose overflow honestly: the server may hold more than one page.
  const overflowCount = data ? Math.max(0, data.total - data.items.length) : 0;
  const view: RenderStateKind = state === "normal" && overflowCount > 0 ? "overflow" : state;

  return (
    <div className="max-w-6xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Package Catalog"
          subtitle="Catalog of skill, contour, bet, frame, and principle packages with lifecycle and deployment state."
          icon="packages"
          actions={
            <Button variant="outline" onClick={reload}>
              <Icon name="reopen" data-icon="inline-start" aria-hidden />
              Refresh
            </Button>
          }
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs value={lens} onValueChange={(value) => setLens(value as CatalogLens)}>
            <TabsList>
              {LENSES.map((value) => (
                <TabsTrigger key={value} value={value} data-testid={`lens-${value}`}>
                  {value === "all" ? "All" : humanize(value)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative w-full max-w-xs">
            <Icon
              name="search"
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Filter by name"
              aria-label="Filter packages by name"
              className="pl-9"
            />
          </div>
        </div>

        <RenderState
          state={view}
          loadingRows={6}
          error={error}
          onRetry={reload}
          overflowCount={overflowCount}
          empty={{
            icon: "packages",
            title: "No authorized packages match.",
            hint:
              lens === "all"
                ? "Publish a package, or clear the name filter to see the full catalog."
                : `No ${humanize(lens)} packages are in the catalog yet.`,
          }}
        >
          {data ? (
            <CatalogWorkspace
              data={{ items: filtered, total: data.total }}
              selected={selected}
              onSelect={(pkg) => setSelectedId(pkg.id)}
              onOpen={(pkg) => go({ to: `/packages/${pkg.id}` })}
            />
          ) : null}
        </RenderState>
      </div>
    </div>
  );
}

export default PackageCatalogPage;
